/**
 * Analytics Facade
 *
 * The single import that all feature code uses for analytics.
 * Registers provider adapters (Mixpanel, GA4, PostHog, ServerRelay, Log)
 * and fans out every call to all active providers.
 *
 * Usage:
 *   import { analytics } from '../services/analytics/analytics';
 *   analytics.track('signup_completed', { method: 'google', invited: false });
 *   analytics.identify(userId, { plan: 'pro', email });
 *   analytics.page('/app/spaces');
 *   analytics.group(orgId, { plan: 'pro', seat_count: 5 });
 *
 * Provider swap: to add/remove a vendor change the providers registered in
 * initAnalytics(). Feature code never needs to change.
 */

import { createFileLogger } from '../../utils/logger';
import type { EventName, EventProps, UserTraits, OrgTraits, PageProps } from './events';
import { VALID_EVENTS } from './events';

const log = createFileLogger('ANALYTICS');

// ---------------------------------------------------------------------------
// Provider interface
// ---------------------------------------------------------------------------
export interface AnalyticsContext {
  userId?: string;
  orgId?: string;
  sessionId?: string;
  plan?: string;
  appVersion?: string;
}

export interface AnalyticsProvider {
  readonly name: string;
  init(ctx: AnalyticsContext): Promise<void>;
  track(event: string, props: Record<string, unknown>): void;
  identify(userId: string, traits?: Record<string, unknown>): void;
  page(path: string, props?: Record<string, unknown>): void;
  group?(orgId: string, traits?: Record<string, unknown>): void;
  reset(): void;
  flush(): Promise<void>;
}

// ---------------------------------------------------------------------------
// Ambient context (enriched on every event automatically)
// ---------------------------------------------------------------------------
interface AmbientContext {
  userId?: string;
  orgId?: string;
  sessionId?: string;
  plan?: string;
  appVersion?: string;
  funnel_id?: string;
  persona?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

// ---------------------------------------------------------------------------
// Debug mode helpers
// ---------------------------------------------------------------------------
const DEBUG_KEY = 'analytics_debug';

function isDebugMode(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location.search.includes('debug_analytics=1')) {
    sessionStorage.setItem(DEBUG_KEY, '1');
    return true;
  }
  return sessionStorage.getItem(DEBUG_KEY) === '1';
}

/**
 * True when events should be sent to remote vendors (Mixpanel, PostHog, GA, server relay).
 * False on localhost and common private LAN hostnames unless `?debug_analytics=1` is active.
 * Use this before calling `posthog.init()` so autocapture cannot bypass the facade.
 */
export function isTrackingEnabled(): boolean {
  // Debug mode overrides the localhost guard
  if (isDebugMode()) return true;

  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname.toLowerCase();
  const isLocal =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname.endsWith('.local') ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.');
  return !isLocal;
}

function debugLog(method: string, ...args: unknown[]) {
  if (isDebugMode()) {
    console.groupCollapsed(`[Analytics:${method}]`, args[0]);
    console.log(...args);
    console.groupEnd();
  }
}

// ---------------------------------------------------------------------------
// UUID helper (used for correlation_id)
// ---------------------------------------------------------------------------
function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ---------------------------------------------------------------------------
// Analytics singleton
// ---------------------------------------------------------------------------
class AnalyticsFacade {
  private providers: AnalyticsProvider[] = [];
  private ambient: AmbientContext = {};
  private sessionId = generateId();

  register(provider: AnalyticsProvider): void {
    this.providers.push(provider);
  }

  /** Call once at app startup after config is loaded. */
  async init(ctx: AnalyticsContext): Promise<void> {
    this.ambient = { ...ctx, sessionId: this.sessionId };
    for (const p of this.providers) {
      try {
        await p.init({ ...ctx, sessionId: this.sessionId });
      } catch (err) {
        log.warn(`[${p.name}] init failed:`, err);
      }
    }
  }

  /** Update ambient context (e.g. after profile loads). */
  setContext(ctx: Partial<AmbientContext>): void {
    this.ambient = { ...this.ambient, ...ctx };
  }

  track<E extends EventName>(event: E, props: EventProps<E>): void {
    if (!isTrackingEnabled()) {
      debugLog('track', event, props);
      return;
    }

    if (!VALID_EVENTS.has(event)) {
      log.warn('Unknown event name:', event);
    }

    const enriched: Record<string, unknown> = {
      ...props,
      correlation_id: generateId(),
      session_id: this.ambient.sessionId,
      ...(this.ambient.userId && { $user_id: this.ambient.userId }),
      ...(this.ambient.orgId && { org_id: this.ambient.orgId }),
      ...(this.ambient.plan && { plan: this.ambient.plan }),
      ...(this.ambient.funnel_id && { funnel_id: this.ambient.funnel_id }),
      ...(this.ambient.persona && { persona: this.ambient.persona }),
      ...(this.ambient.utm_source && { utm_source: this.ambient.utm_source }),
      ...(this.ambient.utm_medium && { utm_medium: this.ambient.utm_medium }),
      ...(this.ambient.utm_campaign && { utm_campaign: this.ambient.utm_campaign }),
    };

    debugLog('track', event, enriched);

    for (const p of this.providers) {
      try {
        p.track(event, enriched);
      } catch (err) {
        log.warn(`[${p.name}] track(${event}) failed:`, err);
      }
    }
  }

  identify(userId: string, traits?: UserTraits): void {
    if (!isTrackingEnabled()) {
      debugLog('identify', userId, traits);
      return;
    }

    this.ambient.userId = userId;
    if (traits?.plan) this.ambient.plan = traits.plan;
    if (traits?.org_id) this.ambient.orgId = traits.org_id;
    if (traits?.funnel_id) this.ambient.funnel_id = traits.funnel_id;
    if (traits?.persona) this.ambient.persona = traits.persona;

    debugLog('identify', userId, traits);

    for (const p of this.providers) {
      try {
        p.identify(userId, traits as Record<string, unknown> | undefined);
      } catch (err) {
        log.warn(`[${p.name}] identify(${userId}) failed:`, err);
      }
    }
  }

  page(path: string, props?: PageProps): void {
    if (!isTrackingEnabled()) {
      debugLog('page', path, props);
      return;
    }

    debugLog('page', path, props);

    const enriched: Record<string, unknown> = {
      ...props,
      session_id: this.ambient.sessionId,
    };

    for (const p of this.providers) {
      try {
        p.page(path, enriched);
      } catch (err) {
        log.warn(`[${p.name}] page(${path}) failed:`, err);
      }
    }
  }

  group(orgId: string, traits?: OrgTraits): void {
    if (!isTrackingEnabled()) {
      debugLog('group', orgId, traits);
      return;
    }

    this.ambient.orgId = orgId;
    debugLog('group', orgId, traits);

    for (const p of this.providers) {
      try {
        p.group?.(orgId, traits as Record<string, unknown> | undefined);
      } catch (err) {
        log.warn(`[${p.name}] group(${orgId}) failed:`, err);
      }
    }
  }

  reset(): void {
    this.ambient = { sessionId: generateId() };
    for (const p of this.providers) {
      try {
        p.reset();
      } catch (err) {
        log.warn(`[${p.name}] reset failed:`, err);
      }
    }
  }

  async flush(): Promise<void> {
    await Promise.all(
      this.providers.map((p) =>
        p.flush().catch((err) => log.warn(`[${p.name}] flush failed:`, err))
      )
    );
  }

  getSessionId(): string {
    return this.sessionId;
  }
}

export const analytics = new AnalyticsFacade();
export default analytics;

// Persist session ID for ServerRelayProvider and the activity middleware
if (typeof sessionStorage !== 'undefined') {
  const existing = sessionStorage.getItem('analytics_session_id');
  if (!existing) {
    sessionStorage.setItem('analytics_session_id', analytics.getSessionId());
  }
}
